<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Narrative"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col cols="4">
          <BasicDate
            :date="section.entry_date"
            :small-columns="12"
            :columns="12"
            :filled="false"
            label="Entry Date"
            required
            outlined
            disabled
            for-party-edit
          />
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            v-model="section.taken_by_id"
            :items="takenByOptions"
            label="Taken by*"
            outlined
            :disabled="!takenByNullAtStart"
            :error-messages="getErrorMessages($v.section.taken_by_id)"
            @input="$v.section.taken_by_id.$touch()"
            @blur="$v.section.taken_by_id.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-textarea
            v-model="section.text"
            label="Narrative*"
            rows="4"
            outlined
            :error-messages="getErrorMessages($v.section.text)"
            @input="$v.section.text.$touch()"
            @blur="$v.section.text.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import BasicDate from '@/components/Fields/BasicDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'NarrativesCard',
  components: { BasicDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      takenByNullAtStart: false,
    }
  },

  validations () {
    return {
      section: {
        entry_date: { required: helpers.withMessage('Entry Date is required', required) },
        taken_by_id: { required: helpers.withMessage('required', required) },
        text: { required: helpers.withMessage('Narrative is required', required) },
      },
    }
  },

  computed: {
    cardIdentifier () { return `narratives-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peNarrativesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeNarrativesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    section () { return this.item },
    sectionIdentifier () { return this.section.id || 'new' },

    takenByOptions () {
      return this.getCachedDropdown('users/login')
    },

    takenByUser () {
      return this.takenByOptions?.
        find(u => u.value === this.section.taken_by_id)?.
        text
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peNarrativesCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {},

  created () {
    if (!this.create) this.initPeNarrativesCard(this.sectionIdentifier)
    if (this.section.taken_by_id == null) this.takenByNullAtStart = true

    this.getDropdownOptions({ path: 'users/login' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peNarrativesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peNarrativesCardCreateUndo()
      else {
        return this.peNarrativesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peNarrativesCardRefresh({ index: this.sectionIdentifier, section: this.section }).
        then(_ => { if (this.section.taken_by_id == null) this.takenByNullAtStart = true })
    },

    async onCardUpdate () {
      if (this.section.taken_by_id == null) this.takenByNullAtStart = true
      return this.peNarrativesCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    onEntryDateUpdate (val) {
      this.$v.section.entry_date.$touch()
      this.section.entry_date = val
    },

    async onSectionUnlock () {
      return this.setPeNarrativesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peNarrativesCardCreateUndo',
      'setPeNarrativesCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeNarrativesCard',
      'peNarrativesCardCreate',
      'peNarrativesCardDelete',
      'peNarrativesCardRefresh',
      'peNarrativesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
